import _ from 'lodash'
import Locale from '@/modules/i18n/model/Locale'
import Message from '@/modules/i18n/model/Message'
import MessagePagination from '@/modules/i18n/model/MessagePagination'
import Axios, { AxiosResponse } from 'axios'
import client from '@/base-http'
import KeyValuePair from '@/modules/core/support/KeyValuePair'

export enum Errors {
    NONE = 0,

    // Errores para localismos
    LOCALE_ALREADY_EXISTS = 1,
    INVALID_IDENTIFIER = 2,
    LOCALE_NOT_FOUND = 3,

    // Errores para mensajes
    MESSAGE_ALREADY_EXISTS = 101,
    MESSAGE_NOT_FOUND = 102,
}

export class I18nError extends Error {
    public message = ''
    public error = Errors.NONE

    constructor (message: string, error: Errors = Errors.NONE) {
        super()
        this.message = message
        this.error = error
    }
}

/**
 * Obtiene la lista de configuraciones regionales registradas en Artisan.
 */
export const getLocales = async (): Promise<Locale[]> => {
    const response = await client.get('/i18n/locale/list')
    const data: any[] = response.data
    const locales: Locale[] = []

    if (!Array.isArray(data)) {
        throw new Error('Se esperaba que la respuesta fuera un array de localismos')
    }

    for (const entry of data) {
        const locale = new Locale(entry.id, entry.identifier, name)
        locale.createdAt = parseInt(entry.createdAt, undefined)
        locale.updatedAt = parseInt(entry.updatedAt, undefined)
        locales.push(locale)
    }

    return locales
}

/**
 * Obtiene un localismo de Artisan en base a un identificador
 *
 * @param locale
 */
export const getLocale = async (locale: string): Promise<Locale> => {
    try {
        const response = await client.get(`i18n/locale/identifier/${locale}`)
        return Locale.parse(response.data)
    } catch (error) {
        const response: AxiosResponse | undefined = error.response

        // Se procesa el error si es "Bad Request" ya que se trata de un error
        // del cliente, datos mal introducidos por ejemplo
        if (response && _.inRange(response.status, 400, 499)) {
            const i18nError = new I18nError(response.data.message)
            switch (response.status) {
                case 404:
                    i18nError.error = Errors.LOCALE_NOT_FOUND
            }

            throw i18nError
        }

        // Se re-lanza el error si no es "Bad Request"
        throw error
    }
}

/**
 * Registra un localismo en Artisan
 *
 * @param locale
 */
export const createLocale = async (locale: Locale): Promise<Locale> => {
    const data: KeyValuePair = {
        identifier: locale.identifier,
    }

    try {
        const response = await client.post('/i18n/locale', data)
        return Locale.parse(response.data)
    } catch (error) {
        const response: AxiosResponse | undefined = error.response
        if (response && _.inRange(response.status, 400, 499)) {
            const i18nError = new I18nError(response.data.message)
            switch (response.status) {
                case 409: i18nError.error = Errors.LOCALE_ALREADY_EXISTS; break
                case 400: i18nError.error = Errors.INVALID_IDENTIFIER
            }

            throw i18nError
        }

        throw error
    }
}

/**
 * Elimina un localismo en Artisan
 *
 * @param locale
 */
export const deleteLocale = async (locale: string): Promise<boolean> => {
    const response = await client.delete(`i18n/locale/${locale}`)

    return response.data.success || false
}

/**
 * Obtiene la lista de mensajes asociada a un localismo
 *
 * @param locale
 */
export const getLocaleMessages = async (locale: string) => {
    const response = await client.get(`i18n/locale/${locale}/message/list`)
    const data = response.data

    return _.map(data, entry => {
        return Message.convert(entry)
    })
}

/**
 * Crea un nuevo mensaje en Artisan
 *
 * @param message
 */
export const createLocaleMessage = async (message: Message) => {

    try {
        const response = await client.post('i18n/message', message.serialize())
        const data = response.data

        if (!data) {
            throw new Error('No se ha recibido información')
        }

        return Message.convert(data)
    } catch (error) {
        const response: AxiosResponse | undefined = error.response
        if (response && _.inRange(response.status, 400, 499)) {
            const i18nError = new I18nError(response.data.message)
            switch (response.status) {
                case 409: i18nError.error = Errors.MESSAGE_ALREADY_EXISTS; break
                case 400: i18nError.error = Errors.LOCALE_ALREADY_EXISTS
            }

            throw i18nError
        }

        throw error
    }
}

/**
 * Actualiza un mensaje asociado a un localismo existente en Artisan
 *
 * @param message
 */
export const updateLocaleMessage = async (message: Message) => {
    const response = await client.put(`i18n/message/${message.key}`, {
        rich_format: message.richFormat,
        translations: message.translations.map(t => t.serialize()),
    })

    return Message.convert(response.data)

}

/**
 * Elimina uno o varios mensajes asociados a un localismo
 *
 * @param {Locale} locale
 * @param {Message[]} messages
 * @returns {Promise<boolean>}
 */
export const deleteMessage = async (messages: Message[]) => {
    const keys = _.map(messages, 'key').join(',')
    const response = await client.delete(`i18n/message/${keys}`)

    return response.data.success || false
}

/**
 * Obtener lista de localismos del mundo
 *
 * Realiza una petición a la API de información regional para obtener una lista con
 * códigos de localismos del mundo junto con su nombre en el idioma
 * argumentado.
 */
export const getWorldLocales = async (language: string): Promise<{ [identifier: string]: string}> => {
    const response = await Axios.get(`https://i18napi.ddns.net/${language}/locales.json`)

    return response.data
}

/**
 * Obtiene un paginación de mensajes desde el servidor
 *
 * @param page
 * @param recordsPerPage
 * @param filters
 */
export const getMessagePagination = async (page = 1, recordsPerPage = 15, filters: KeyValuePair = {}) => {
    const response = await client.get('i18n/messages/pagination', {
        params: { page, recordsPerPage, ...filters },
    })

    return MessagePagination.deserialize(response.data)
}
